import React from 'react'
// libs
import styled from 'styled-components'
import { useSelector } from 'react-redux'
import { FieldArray, formValueSelector } from 'redux-form'
// graphql
import { RELATED_FORMS_USERS_SELECT_QUERY } from 'graphql/risk/queries'
// components
import renderUsers from 'components/Modals/GroupSecurityModal/Form/BusinessRule/components/UserPermissions'
// types
import { QueryResult } from 'types/apollo'
import {
  IField,
  FieldType,
  FieldSource,
  IRiskTemplate,
  IRiskTemplatesResponse,
  IRiskTemplatesQueryArgs
} from 'types/graphql'
// context
import { RiskTemplateAPIContext } from 'containers/Risks/RiskTemplate/Edit/layers/API'
// constants
import { Forms } from 'constants/forms'
import { SELECT_GROUP_LABELS } from 'constants/forms'
import { RECIPIENT_ENTITY_TYPE } from 'constants/systemProcesses'
// utils
import get from 'lodash/get'
import { userAndPositionProps } from 'components/FormElements/ReduxForm/ReduxEntitySelect/utils'

const UsersWrapper = styled.div`
  grid-column: span 3;
`

const selector = formValueSelector(Forms.SYSTEM_PROCESS_CREATE_FORM)

interface Props {
  name?: string
  field: string
}

const UsersPermissions: React.FC<Props> = ({ name = 'businessRules', field }) => {
  const { isRiskTemplate, riskTemplate } = React.useContext(RiskTemplateAPIContext)

  const users = useSelector((state) => selector(state, `${field}.businessRules`) || [])

  const usersIds = users.map(({ user }) => get(user, 'value'))

  const templateFields: IField[] = get(riskTemplate, 'fields', []).filter(
    ({ fieldType, source }) =>
      fieldType === FieldType.Lookup && source.some((src) => src === FieldSource.Users || src === FieldSource.Positions)
  )

  const riskTemplatesIds = templateFields
    .filter(({ entityId }) => entityId !== riskTemplate.id)
    .map(({ entityId }) => entityId)

  const variables = { riskTemplatesFilter: { ids: riskTemplatesIds } }

  const getFieldsOptions = ({ data }: { data: QueryResult<IRiskTemplatesResponse, IRiskTemplatesQueryArgs> }) => {
    const riskTemplates: IRiskTemplate[] = get(data, 'riskTemplates.data', []).filter(
      ({ id }) => id !== riskTemplate.id
    )

    return templateFields
      .map((field: IField) => {
        const parentTemplate = riskTemplates?.find(({ id }) => id === field.entityId)

        const parentLabel = field.parentFieldId
          ? `${riskTemplate?.fields?.find((f) => f.id === field.parentFieldId)?.label}: `
          : ''

        const label = parentTemplate
          ? `${parentTemplate.name} - ${parentLabel} ${field.label}`
          : `${parentLabel} ${field.label}`
        const option = { label, value: field.id, entity: RECIPIENT_ENTITY_TYPE.FIELD }

        return [option]
      })
      .flat()
  }

  const getUserOptions = (data) => {
    const userOptions = [{ label: '(All Users)', value: 'ALL' }, ...userAndPositionProps.getOptions(data)]

    if (isRiskTemplate) {
      const fieldsOptions = getFieldsOptions(data)

      return [
        { label: SELECT_GROUP_LABELS.FIELDS, options: fieldsOptions },
        { label: SELECT_GROUP_LABELS.USERS_AND_POSITIONS, options: userOptions }
      ]
    }

    return userOptions
  }

  return (
    <UsersWrapper>
      <FieldArray<any>
        isRelatedForms
        usersIds={usersIds}
        variables={variables}
        name={`${field}.${name}`}
        component={renderUsers as any}
        getOptions={getUserOptions}
        query={RELATED_FORMS_USERS_SELECT_QUERY}
      />
    </UsersWrapper>
  )
}

export default UsersPermissions
